import { B } from "../constants";

export default function PrivacyPolicy() {
  return (
    <div style={{minHeight:"100vh",background:B.bg,color:B.textSec,fontFamily:"IBM Plex Sans,sans-serif",padding:"40px 24px",maxWidth:640,margin:"0 auto"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <a href="/" style={{fontSize:11,letterSpacing:"0.2em",color:B.accent,textTransform:"uppercase",fontWeight:600,textDecoration:"none",fontFamily:"IBM Plex Mono,monospace"}}>FYNWELL</a>
      <h1 style={{fontSize:22,fontWeight:600,color:B.text,margin:"16px 0 24px",fontFamily:"IBM Plex Mono,monospace"}}>Privacy Policy</h1>
      <p style={{fontSize:11,color:B.muted,marginBottom:24}}>Last updated: March 19, 2026</p>

      <Section title="What we collect">
        <p>When you use Fynwell, we collect:</p>
        <ul>
          <li><strong>Account credentials</strong> - email and password via Supabase Auth</li>
          <li><strong>Financial data</strong> - bank account balances, transactions, and liabilities synced through Plaid. We never see or store your bank login credentials.</li>
          <li><strong>Usage data</strong> - which features you use and how you interact with insights, collected via PostHog analytics</li>
          <li><strong>Chat messages</strong> - conversations with the Fynwell assistant, used to provide personalized financial intelligence</li>
        </ul>
      </Section>

      <Section title="Why we collect it">
        <ul>
          <li>To provide personalized financial insights for your household</li>
          <li>To detect spending patterns, idle cash, subscription waste, and other opportunities</li>
          <li>To improve the quality and relevance of our recommendations</li>
          <li>To separate business (LLC) and personal expenses for tax purposes</li>
        </ul>
      </Section>

      <Section title="What we never do">
        <ul>
          <li>We never sell your data to third parties</li>
          <li>We never share your financial data with advertisers</li>
          <li>We never send dollar amounts, account data, or behavioral signals via WhatsApp or push notifications</li>
          <li>Our AI assistant never sees your raw financial history - only structured, summarized insight packets</li>
        </ul>
      </Section>

      <Section title="Plaid data handling">
        <p>Fynwell uses Plaid to connect your bank accounts. When you connect an account through Plaid:</p>
        <ul>
          <li>Plaid securely handles your bank login - we never see or store your banking credentials</li>
          <li>We receive read-only access to account balances and transaction history</li>
          <li>We store a Plaid access token to maintain the connection - this token cannot be used to move money or modify your accounts</li>
          <li>You can disconnect any linked account at any time from within the app, which revokes the Plaid access token</li>
          <li>When you delete your Fynwell account, all Plaid connections and stored tokens are permanently removed</li>
        </ul>
        <p>For more information on how Plaid handles your data, visit <a href="https://plaid.com/legal" style={{color:B.cyan,textDecoration:"none"}} target="_blank" rel="noopener noreferrer">plaid.com/legal</a>.</p>
      </Section>

      <Section title="Data security">
        <ul>
          <li>All data is encrypted in transit (TLS) and at rest</li>
          <li>Plaid access tokens are stored in a secure vault, never in application tables</li>
          <li>Row-level security (RLS) on all database tables prevents cross-household data access</li>
          <li>Infrastructure hosted on SOC 2 compliant providers (Supabase, Vercel)</li>
          <li>Service available to US residents only</li>
        </ul>
      </Section>

      <Section title="How to delete your data">
        <p>You can delete your account and all associated data at any time from the app:</p>
        <ol>
          <li>Open Fynwell and sign in</li>
          <li>Tap "Delete account" on the home screen</li>
          <li>Confirm the deletion</li>
        </ol>
        <p>This permanently removes all your financial data, chat history, insights, and Plaid connections. You can also choose "forget mode" which anonymizes behavioral data for product improvement while deleting all personally identifiable information.</p>
        <p>You can export all your data at any time using the "Export data" button on the home screen.</p>
      </Section>

      <Section title="Do not sell or share my personal information" id="do-not-sell">
        <p>Fynwell does not sell or share personal information for advertising. We share data only with Plaid (account connection), Supabase (storage), PostHog (analytics), and Hume AI (voice synthesis, optional opt-in with separate consent).</p>
        <p>To request deletion of your data, contact us at <a href="mailto:privacy@getfynwell.ai" style={{color:B.cyan,textDecoration:"none"}}>privacy@getfynwell.ai</a>.</p>
      </Section>

      <Section title="Contact">
        <p>For privacy questions, data requests, or concerns:</p>
        <p style={{marginTop:8}}><strong>Email:</strong> <a href="mailto:hello@getfynwell.ai" style={{color:B.cyan,textDecoration:"none"}}>hello@getfynwell.ai</a></p>
        <p><strong>Entity:</strong> SAVD BY AI LLC</p>
      </Section>
    </div>
  );
}

function Section({ title, children, id }) {
  return (
    <div id={id} style={{marginBottom:28}}>
      <h2 style={{fontSize:15,fontWeight:600,color:B.text,marginBottom:10,fontFamily:"IBM Plex Mono,monospace"}}>{title}</h2>
      <div style={{fontSize:13,lineHeight:1.7,color:B.textSec}}>{children}</div>
      <style>{`
        div ul, div ol { padding-left: 20px; margin: 8px 0; }
        div li { margin-bottom: 6px; }
        div p { margin-bottom: 8px; }
      `}</style>
    </div>
  );
}
